/**
 * Cookie-consent storage layer.
 *
 * Keeps backwards compatibility with the legacy `vanilla-cookieconsent`
 * deployment by reusing the same cookie name (`prostay_cookie_consent`)
 * and the same `categories: string[]` shape that `ConditionalAnalytics`
 * already reads. We extend the payload with a few extra fields
 * (version, acceptedAt) so we can revoke consent later when our cookie
 * usage materially changes.
 *
 * Cookie format
 * -------------
 *   {
 *     "version": 2,
 *     "categories": ["necessary", "functional", "analytics", "marketing"],
 *     "acceptedAt": "2026-04-26T10:00:00.000Z"
 *   }
 *
 * URL-encoded, single cookie, 365-day expiry. We deliberately keep
 * everything in one cookie to avoid surprising the user with extra
 * tracking primitives before they have consented to anything.
 */

export const COOKIE_NAME = 'prostay_cookie_consent';
export const COOKIE_VERSION = 2;
export const COOKIE_EXPIRY_DAYS = 365;

/* The order below drives the rendering order in the preferences modal.
 * `necessary` is always on and cannot be toggled. Everything else is
 * opt-in (off by default, must be enabled explicitly). */
export const CATEGORIES = ['necessary', 'functional', 'analytics', 'marketing'];

/* Custom DOM events. Names mirror the legacy `vanilla-cookieconsent`
 * contract (`cc:onConsent`, `cc:onChange`) so downstream listeners
 * (most notably `ConditionalAnalytics`) keep working without edits. */
export const CONSENT_EVENT = 'cc:onConsent';
export const CHANGE_EVENT = 'cc:onChange';

/* Custom open/close requests. The footer link dispatches OPEN_EVENT and
 * the consent component listens for it. */
export const OPEN_EVENT = 'cc:requestOpen';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

function readCookie() {
  if (!isBrowser) return null;
  const raw = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));
  if (!raw) return null;
  try {
    const value = decodeURIComponent(raw.split('=')[1] || '');
    if (!value) return null;
    const parsed = JSON.parse(value);
    if (!parsed || !Array.isArray(parsed.categories)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCookie(payload) {
  if (!isBrowser) return;
  const expires = new Date();
  expires.setTime(
    expires.getTime() + COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000
  );
  /* SameSite=Lax is the right default — the cookie is needed for
   * first-party use only, never on cross-site POSTs. Secure flag is
   * added on https origins so we never leak it over HTTP. */
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const value = encodeURIComponent(JSON.stringify(payload));
  document.cookie =
    `${COOKIE_NAME}=${value}` +
    `; expires=${expires.toUTCString()}` +
    `; path=/` +
    `; SameSite=Lax${secure}`;
}

function dispatchEvent(name, detail) {
  if (!isBrowser) return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

/**
 * Push a Google Consent Mode v2 update into `dataLayer`.
 *
 * GTM is server-rendered (see `GoogleTagManagerHead`) with all
 * consent signals defaulted to 'denied'. Whenever the user makes a
 * choice — accept all, reject all, or a category-by-category save in
 * the preferences modal — we mirror that choice into Consent Mode so
 * GTM-managed tags (GA4, Ads, Floodlight, etc.) immediately fire or
 * stop firing per the user's preference.
 *
 * Mapping (intentionally conservative, matches Google's published
 * ePrivacy guidance for EU traffic):
 *   - `analytics`  → `analytics_storage`
 *   - `marketing`  → `ad_storage`, `ad_user_data`, `ad_personalization`
 *   - `functional` → `functionality_storage`, `personalization_storage`
 *   - `necessary`  → always granted (`security_storage`)
 *
 * Safe to call even if GTM is not configured — the push lands in a
 * stub `dataLayer` that nothing reads.
 */
export function syncConsentMode(categories) {
  if (!isBrowser) return;
  if (!Array.isArray(categories)) return;

  /* Defensive init — the head bootstrap already creates dataLayer
   * and the gtag shim, but we never want this helper to throw if it
   * runs before that script (e.g. in a test or storybook). */
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }

  const has = (c) => categories.includes(c);

  gtag('consent', 'update', {
    analytics_storage: has('analytics') ? 'granted' : 'denied',
    ad_storage: has('marketing') ? 'granted' : 'denied',
    ad_user_data: has('marketing') ? 'granted' : 'denied',
    ad_personalization: has('marketing') ? 'granted' : 'denied',
    functionality_storage: has('functional') ? 'granted' : 'denied',
    personalization_storage: has('functional') ? 'granted' : 'denied',
    security_storage: 'granted',
  });
}

/**
 * Public read API.
 *
 * Returns:
 *   - null when no choice has been made yet (banner should show).
 *   - { categories, version, acceptedAt } when a choice exists.
 *
 * Older cookie versions are migrated forward in place: prior consent
 * is preserved (so returning users are not re-prompted) but new
 * categories introduced after the original choice default to OFF.
 * This is the GDPR-friendly path — we never silently grant a category
 * the user has not yet seen.
 */
export function getStoredConsent() {
  const cookie = readCookie();
  if (!cookie) return null;

  const known = cookie.categories.filter((c) => CATEGORIES.includes(c));
  const storedVersion = cookie.version || 1;

  if (storedVersion < COOKIE_VERSION) {
    /* Migrate forward, persist, but do NOT dispatch a change event —
     * this is a transparent upgrade, not a new user choice. */
    const migrated = {
      version: COOKIE_VERSION,
      categories: Array.from(new Set(['necessary', ...known])),
      acceptedAt: cookie.acceptedAt || new Date().toISOString(),
    };
    writeCookie(migrated);
    return migrated;
  }

  return {
    version: cookie.version,
    categories: known,
    acceptedAt: cookie.acceptedAt || null,
  };
}

export function hasConsented(category) {
  const stored = getStoredConsent();
  if (!stored) return false;
  return stored.categories.includes(category);
}

/**
 * Persist a consent choice and notify listeners.
 *
 * `categories` should always include `necessary` — we add it
 * defensively here so callers cannot accidentally strip it.
 */
export function setStoredConsent(categories, { isFirst = false } = {}) {
  const normalized = Array.from(
    new Set(['necessary', ...categories.filter((c) => CATEGORIES.includes(c))])
  );
  const payload = {
    version: COOKIE_VERSION,
    categories: normalized,
    acceptedAt: new Date().toISOString(),
  };
  writeCookie(payload);
  /* Mirror the choice into Google Consent Mode so GTM-managed tags
   * react in real time. Must run before listeners — they may rely on
   * gtag side-effects (e.g. dataLayer events) being in place. */
  syncConsentMode(normalized);
  dispatchEvent(isFirst ? CONSENT_EVENT : CHANGE_EVENT, payload);
  /* Also fire the generic CONSENT_EVENT on first-write because the
   * legacy library historically fired both. Keeping the contract makes
   * `ConditionalAnalytics`-style listeners simpler. */
  if (isFirst) dispatchEvent(CHANGE_EVENT, payload);
  return payload;
}

/* Convenience accept-all / reject-all helpers used by the banner. */
export function acceptAll(opts) {
  return setStoredConsent(CATEGORIES, opts);
}

export function rejectAll(opts) {
  return setStoredConsent(['necessary'], opts);
}

/**
 * Imperatively open the preferences modal from anywhere in the tree.
 * Used by the footer "Cookie settings" link and by the dev-only
 * `window.showCookiePreferences` shim.
 */
export function openPreferences() {
  if (!isBrowser) return;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

/**
 * Wipe the consent cookie completely.
 *
 * Used by `window.cookieConsentReset()` (dev/QA helper). After
 * calling, callers normally `location.reload()` so the component
 * re-runs its mount effect and shows the banner.
 */
export function clearStoredConsent() {
  if (!isBrowser) return;
  document.cookie =
    `${COOKIE_NAME}=` +
    `; expires=Thu, 01 Jan 1970 00:00:00 GMT` +
    `; path=/`;
}
